import { h, Fragment } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';

import * as OptionController from '../../../controllers/OptionsController';

import { GlobalState } from '../../../states/global-state';

import type { FormSelectControl } from '../../../types/form-config';
import { effect, useSignal, useSignalEffect } from '@preact/signals';
import { FormState } from '../../../states/kyc-form-state';
import Store from '../../../stores/Store';
import { KycProps } from '../../../constants/kyc';

import type { KycLoadDataType } from '../../../types/types';
import { showSpinner, hideSpinner } from '../../../utils';

type AddressSelectProps = {
  country: FormSelectControl;
  province: FormSelectControl;
  city: FormSelectControl;
};

const AddressSelect = props => {
  const store = useMemo(() => new Store(), []);
  const [optionsCountry, setCountryOptions] = useState([]);
  const [optionsProvince, setProvinceOptions] = useState([]);
  const [optionsCity, setCityOptions] = useState([]);

  const currentKycLoad = useRef({});
  const formControls = useRef(props.controls as AddressSelectProps);

  const loadedKyc = useSignal(false);
  const loadedCountry = useSignal(false);
  const loadedProvince = useSignal(false);
  const loadedCity = useSignal(false);
  const selectedCountry: any = useSignal('');
  const selectedProvince: any = useSignal('');
  const selectedCity: any = useSignal('');

  const isChanged = useRef(null);

  const isLoaded = sig => {
    setTimeout(() => {
      sig.value = true;
    }, 500);
  };

  const getCountries = async controls => {
    const options = await OptionController[controls.country.optionGetter]({
      category: controls.country.optionCategory,
    });

    if (options) {
      setCountryOptions(options);

      isLoaded(loadedCountry);
    }
  };
  const getProvinces = async (controls, state) => {
    if (!state) {
      isLoaded(loadedProvince);
      return;
    }

    const options = await OptionController[controls.province.optionGetter]({
      category: controls.province.optionCategory,
      param: state,
    });

    if (options) {
      setProvinceOptions(options);
      isLoaded(loadedProvince);
    }
  };

  const getCities = async (controls, province) => {
    if (!province) {
      isLoaded(loadedCity);
    }
    const options = await OptionController[controls.city.optionGetter]({
      category: controls.city.optionCategory,
      param: province,
    });

    if (options) {
      setCityOptions(options);

      isLoaded(loadedCity);
    }
  };

  useSignalEffect(() => {
    if (isChanged.current == null) {
      isChanged.current = FormState.sameAddress.value;
      return;
    }

    if (FormState.sameAddress.value) {
      //copy stored home to office
      store.getFormData(null, (items: KycLoadDataType) => {
        if (!items) {
          return;
        }

        changeCountryHandler({
          target: { value: items[KycProps.COUNTRY_HOME] },
        });
        changeProvinceHandler({
          target: { value: items[KycProps.PROVINCE_HOME] },
        });
        changeCityHandler({ target: { value: items[KycProps.CITY_HOME] } });

        FormState.formControlValues[KycProps.ADDRESS_OFFICE].value =
          items[KycProps.ADDRESS_HOME];
        FormState.formControlValues[KycProps.FAX_OFFICE].value =
          items[KycProps.FAX_HOME];
        FormState.formControlValues[KycProps.POSTAL_OFFICE].value =
          items[KycProps.POSTAL_HOME];
        FormState.formControlValues[KycProps.PHONE_OFFICE].value =
          items[KycProps.PHONE_HOME];
      });
    } else {
      //copy stored office to office;
      store.getFormData(null, items => {
        // console.log(105, items);
        if (!items) {
          return;
        }
        changeCountryHandler({
          target: { value: items[KycProps.COUNTRY_OFFICE] },
        });
        changeProvinceHandler({
          target: { value: items[KycProps.PROVINCE_OFFICE] },
        });
        changeCityHandler({ target: { value: items[KycProps.CITY_OFFICE] } });
        FormState.formControlValues[KycProps.ADDRESS_OFFICE].value =
          items[KycProps.ADDRESS_OFFICE];
        FormState.formControlValues[KycProps.FAX_OFFICE].value =
          items[KycProps.FAX_OFFICE];
        FormState.formControlValues[KycProps.POSTAL_OFFICE].value =
          items[KycProps.POSTAL_OFFICE];
        FormState.formControlValues[KycProps.PHONE_OFFICE].value =
          items[KycProps.PHONE_OFFICE];
      });
    }
  });

  useSignalEffect(() => {
    const kycLoad = GlobalState.kycLoad.value || {};

    currentKycLoad.current = kycLoad;
    if (Object.keys(kycLoad).length) {
      loadedKyc.value = true;

      loadedCountry.value &&
        (selectedCountry.value = kycLoad[formControls.current.country.name]);
      loadedProvince.value &&
        (selectedProvince.value = kycLoad[formControls.current.province.name]);
      loadedCity.value &&
        (selectedCity.value = kycLoad[formControls.current.city.name]);
    }
    if (loadedKyc.value && loadedCountry.value) {
      getProvinces(
        formControls.current,
        kycLoad[formControls.current.country.name]
      );
    }

    if (loadedKyc.value && loadedCountry.value && loadedProvince.value) {
      getCities(
        formControls.current,
        kycLoad[formControls.current.province.name]
      );
    }

    if (
      loadedKyc.value &&
      loadedCountry.value &&
      loadedProvince.value &&
      loadedCity.value
    ) {
      hideSpinner();
    } else {
      showSpinner();
    }
  });

  useEffect(() => {
    const controls = formControls.current;

    getCountries(controls);
  }, []);

  const changeCountryHandler = e => {
    selectedCountry.value = e.target.value;
    getProvinces(formControls.current, e.target.value);
  };

  const changeProvinceHandler = e => {
    selectedProvince.value = e.target.value;
    getCities(formControls.current, e.target.value);
  };
  const changeCityHandler = e => {
    selectedCity.value = e.target.value;
  };

  return (
    <Fragment>
      <div
        class="form-floating mb-3 form-group"
        key={formControls.current.country['id']}
      >
        <select
          class="form-select input"
          id={formControls.current.country.name}
          name={formControls.current.country.name}
          data-validator={formControls.current.country.validator || ''}
          value={selectedCountry}
          onChange={changeCountryHandler}
        >
          {optionsCountry.map(item => {
            return (
              <option value={item.code} key={item.key}>
                {item.value}
              </option>
            );
          })}
        </select>
        <label for={formControls.current.country.name}>
          {formControls.current.country.label}
        </label>
      </div>
      <div
        class="form-floating mb-3 form-group"
        key={formControls.current.province['id']}
      >
        <select
          class="form-select input"
          id={formControls.current.province.name}
          name={formControls.current.province.name}
          data-validator={formControls.current.province.validator || ''}
          value={selectedProvince}
          onChange={changeProvinceHandler}
        >
          {optionsProvince.map(item => {
            return (
              <option value={item.code} key={item.key}>
                {item.value}
              </option>
            );
          })}
        </select>
        <label for={formControls.current.province.name}>
          {formControls.current.province.label}
        </label>
      </div>
      <div
        class="form-floating mb-3 form-group"
        key={formControls.current.city['id']}
      >
        <select
          class="form-select input"
          id={formControls.current.city.name}
          name={formControls.current.city.name}
          data-validator={formControls.current.city.validator || ''}
          value={selectedCity}
          onChange={changeCityHandler}
        >
          {optionsCity.map(item => {
            return (
              <option value={item.code} key={item.key}>
                {item.value}
              </option>
            );
          })}
        </select>
        <label for={formControls.current.city.name}>
          {formControls.current.city.label}
        </label>
      </div>
    </Fragment>
  );
};

export default AddressSelect;
